// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDataSheet from 'react-datasheet';

// Import Pages
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Material UI
import { Button } from '@material-ui/core';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutline from '@material-ui/icons/RemoveCircleOutline';

// Import CSS
import 'react-datasheet/lib/react-datasheet.css';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Param       variableRenbder - variable render
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
function InsertRow({ action, variables, setOpenVariable, visible }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState(action.fields.tab?.value ? action.fields.tab.value : 'first');
    const [data, setData] = useState(action.fields.row?.value ? [action.fields.row?.value] : [['', '']]);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const label = 'CONTENT.CONFIGURATION-FORM.DATA-TABLE.INSERT-ROW.';

    useEffect(() => {
        // Assign initial values
        if (!action.fields.tab) {
            action.fields.tab = { value: 'first' };
            setTab('first');
        } else {
            setTab(action.fields.tab.value);
        }
        if (!action.fields.row) { action.fields.row = { value: ['', ''], type: 'ARRAY' }; }
        if (action.fields.row) { action.fields.row.type = "ARRAY" }
    }, [action.id]);

    // Handle tab change
    const handleClick = (e) => {
        let value = e.target.value;
        action.fields.tab = { value };

        if (value !== 'specific') {
            delete action.fields.index;
        }

        const field = initialActionsData[action.id]?.fields.tab;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("tab", action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);
        setTab(value);
    }
    // const displayData = (x, y) => {
    //     return data?.[y]?.[x];
    // };
    // // Handle table change
    // const onChange = (changes) => {
    //     const newData = [...data];
    //     for (const change of changes) {
    //         if (!newData[change.y]) {
    //             newData[change.y] = [];
    //         }
    //         newData[change.y][change.x] = change.value;
    //     }
    //     action.fields.row = { value: newData };
    //     setData(newData);
    // };
    // Handle cell value changes
    const onCellsChanged = changes => {
        const grid = data.map(row => [...row]);
        changes.forEach(({ cell, row, col, value }) => {
            grid[row][col] = value;
        });
        action.fields.row.value = grid[0];

        const field = initialActionsData[action.id]?.fields.row;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("row", action, oldValue, grid[0], setSaveAvailable, setChangeFields, changedFieldsState);

        // Update state
        setData(grid);
    }
    // Handle removing row
    const handleAddColumn = (e) => {
        for (const item of data) {
            // Push row values
            item.push('');
        }
        const field = initialActionsData[action.id]?.fields.row;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("row", action, oldValue, data[0], setSaveAvailable, setChangeFields, changedFieldsState);
        setData([...data]);
    }
    // Handle removing column
    const handleRemoveColumn = (e) => {
        if (data[0].length > 1) {
            for (const item of data) {
                // Remove row values
                item.pop();
            }
            action.fields.row.value = data[0];

            const field = initialActionsData[action.id]?.fields.row;
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated("row", action, oldValue, data[0], setSaveAvailable, setChangeFields, changedFieldsState);

            setData([...data]);
        }
    }
    return (
        <div>
            <div className={`conf-field-container ${visible ? "display-none" : ""}`}>
                <FieldLabel name={t(label + 'SOURCE')} is_required />
                <VariablePicker action={action} inputName="assignSource" variables={variables} setOpenVariable={setOpenVariable} filter="data table" isRequired hideRequired />
                <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
            </div>

            <div className="conf-field-container">
                <FieldLabel name={t(label + 'INDEXES')} />
                <div className="condition-tabs">
                    <button className={`f-s-11 ${tab === 'first' ? 'condition-active' : ''}`} value="first" onClick={handleClick}>
                        {t(label + 'FIRST')}
                    </button>
                    <button className={`f-s-11 ${tab === 'last' ? 'condition-active' : ''}`} value="last" onClick={handleClick}>
                        {t(label + 'LAST')}
                    </button>
                    <button className={`f-s-11 ${tab === 'specific' ? 'condition-active' : ''}`} value="specific" onClick={handleClick}>
                        {t(label + 'SPECIFIC')}
                    </button>
                </div>
                {tab === 'specific' &&
                    <div className="conf-field-container">
                        <TextInput action={action} inputName="index" variables={variables} setOpenVariable={setOpenVariable}
                            fieldType="number" displayName={t(label + 'SPECIFIC')} isRequired />
                    </div>
                }
            </div>

            <div className="conf-field-container">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <FieldLabel name={t(label + 'ROW')} />
                    <div>
                        <Button onClick={handleAddColumn} size='small'><AddCircleOutline className='w-h-20' /></Button>
                        <Button onClick={handleRemoveColumn} size='small'><RemoveCircleOutline className='w-h-20' /></Button>
                    </div>
                </div>
                <ReactDataSheet
                    data={data}
                    valueRenderer={cell => cell}
                    onCellsChanged={onCellsChanged}
                />
            </div>
        </div>
    );
}
export default InsertRow;
